import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { combineLatest, EMPTY, forkJoin, distinctUntilChanged, map, of, Subscription } from 'rxjs';
import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { OfferPromo, PromoDomain, OfferPromoService, OfferGroupDomain, Offer, SettingsService } from '../..';
import { Status } from '../../models/offer-promo-status';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CommentsComponent } from 'pr1-ui-components';
import { MatTableDataSource } from '@angular/material/table';
import { DOCUMENT } from '@angular/common';
import { catchError } from 'rxjs/operators';
import { EventWorkflowService } from '../../services/event-workflow.service';
import { OfferCopy } from '../../models/offer-copy.model';
import { WorkflowStage } from '../../models/workflow-stage.model';
import { ListViewStateService } from '../../services/list-view-state.service';
import {
  buildOfferDisplayRows,
  commentsAnchorIdForOffer,
  getNonBaseVariants,
  isExpandableOffer,
  OfferDisplayRow,
  trackOfferDisplayRow,
} from '../../configs/offer-variant-rows.config';

const log = new Logger('OfferListCardsComponent');

@Component({
  selector: 'app-offer-list-cards',
  templateUrl: './offer-list-cards.component.html',
  styleUrls: ['./offer-list-cards.component.scss'],
})
export class OfferListCardsComponent<TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>> implements OnInit, OnDestroy {
  workflowStages: WorkflowStage[] = [];
  @Input() set readOnly(val: boolean) {
    this.readOnlyMode = val;
    this.offerpromoService.setOfferDetailsReadOnly(val);
  }

  @Input() set variantViewMode(val: 'compressed' | 'expanded') {
    const prev = this._variantViewMode;
    this._variantViewMode = val;
    if (prev === val) {
      return;
    }
    this.rebuildDisplayRows();
    if (val === 'expanded') {
      this.startExpandedVariantLoads();
    }
  }

  get variantViewMode(): 'compressed' | 'expanded' {
    return this._variantViewMode;
  }

  private _variantViewMode: 'compressed' | 'expanded' = 'compressed';

  @Input() set modifiedItems(ids: string[] | null | undefined) {
    this._modifiedItemSet = new Set((ids ?? []).filter((id) => !!id));
  }

  private _modifiedItemSet = new Set<string>();

  @Output() contentRendered = new EventEmitter<void>();
  @Output() viewSelected = new EventEmitter<void>();

  Status: typeof Status = Status;
  PreviewMode = PreviewMode;
  viewModel$ = combineLatest([
    this.offerpromoService.getAllEventOfferTypesByEventId$,
    this.offerpromoService.workflowStages$
  ]).pipe(
    map(([eventOfferTypes, workflowStages]) => {
      if (this.ewService.readOnlyMode != null) {
        this.readOnlyMode = this.ewService.readOnlyMode;
      }
      this.eventOfferTypes = new MatTableDataSource(eventOfferTypes);
      this.syncSelectionWithDataSource();
      this.workflowStages = workflowStages;
      this.rebuildDisplayRows();
      if (this._variantViewMode === 'expanded') {
        this.startExpandedVariantLoads();
      }
      requestAnimationFrame(() => {
        this.contentRendered.emit();
      });
      return { eventOfferTypes };
    })
  );

  eventOfferTypes: MatTableDataSource<OfferGroupDomain<Offer>> = new MatTableDataSource();
  displayRows: OfferDisplayRow[] = [];
  selection = new SelectionModel<OfferGroupDomain<Offer>>(true, [], true, (o1, o2) => o1.DomainId === o2.DomainId);
  readOnlyMode = true;
  isCommentDialogOpened = false;
  private subscriptions: Subscription[] = [];
  showComments: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    public utilityService: UtilityService,
    private dialog: MatDialog,
    private ewService: EventWorkflowService,
    private settingService: SettingsService,
    private listViewState: ListViewStateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    log.debug('init');

    this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
    this.subscriptions.push(
      this.offerpromoService.commentDialogBoxStatus$.subscribe((status: boolean) => {
        this.isCommentDialogOpened = status;
      }));
    this.subscriptions.push(
      this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
        this.readOnlyMode = res;
      }));

    this.subscriptions.push(
      this.listViewState.listViewState$
        .pipe(
          map(s => (s.selectedRecords ?? []).map(r => String(r?.DomainId ?? '')).sort().join('|')),
          distinctUntilChanged(),
        )
        .subscribe(() => this.syncSelectionWithDataSource())
    );

    this.getIntegrations()
  }

  get selectedCount(): number {
    return this.listViewState.listViewStateValue.selectedRecords?.length ?? 0;
  }

  onViewSelected(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.viewSelected.emit();
  }

  getIntegrations() {
    this.settingService.getIntegrations().subscribe((res: any) => {
      this.settingService.addIntegrations(res);
      const conmmentsConfig = res.find((i: any) => i.ClientKey === 'retailercomments');
      if (conmmentsConfig) {
        this.showComments = conmmentsConfig.Status;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private rebuildDisplayRows(): void {
    this.displayRows = buildOfferDisplayRows(this.eventOfferTypes?.data ?? [], this._variantViewMode);
  }

  /**
   * First time expanded: show skeleton rows and call GetPreview (getVariantDetails) for each non-base variant.
   * Cached cards on each offer object — collapse/expand reuses without new API calls.
   */
  private startExpandedVariantLoads(): void {
    if (this._variantViewMode !== 'expanded') {
      return;
    }
    const data = this.eventOfferTypes?.data ?? [];
    let markedLoading = false;

    for (const offer of data) {
      if (!isExpandableOffer(offer)) {
        continue;
      }
      const nonBase = getNonBaseVariants(offer);
      if (nonBase.length === 0) {
        continue;
      }
      const cached = offer['_expandedVariantCards'] as OfferGroupDomain<Offer>[] | undefined;
      if (cached?.length) {
        continue;
      }
      if (offer['_expandedVariantsLoading']) {
        continue;
      }

      offer['_expandedVariantsLoading'] = true;
      markedLoading = true;

      const et = offer.EventOfferType!;
      const requests = nonBase.map((v) =>
        this.offerpromoService.getVariantDetails(v.Id!, et).pipe(
          catchError((error) => {
            log.error('Error fetching variant details (GetPreview)', error);
            return of(null);
          }),
          map((response) => {
            if (!response) {
              return null;
            }
            const card = JSON.parse(JSON.stringify(offer)) as OfferGroupDomain<Offer>;
            card['_isVariantCard'] = true;
            card['_variantId'] = v.Id;
            if (v.Id && card.Detail) {
              card.Detail.Id = v.Id;
            }
            Object.keys(response).forEach((key) => {
              if (key !== 'Variants' && key !== 'VariantsCount') {
                card[key] = (response as any)[key];
              }
            });
            return card;
          }),
        ),
      );

      forkJoin(requests).subscribe((results) => {
        offer['_expandedVariantCards'] = results.filter(Boolean) as OfferGroupDomain<Offer>[];
        offer['_expandedVariantsLoading'] = false;
        this.rebuildDisplayRows();
        this.cdr.markForCheck();
      });
    }

    if (markedLoading) {
      this.rebuildDisplayRows();
      this.cdr.markForCheck();
    }
  }

  trackDisplayRow(index: number, row: OfferDisplayRow): string {
    return trackOfferDisplayRow(index, row);
  }

  fetchVariantDetails(variantId: string, offerType: OfferGroupDomain<Offer>): void {
    if (variantId && offerType.EventOfferType && offerType.EventOfferType !== 'OFFERGROUP') {
      this.offerpromoService
        .getVariantDetails(variantId, offerType.EventOfferType)
        .pipe(
          catchError(error => {
            log.error('Error fetching variant details', error);
            return [];
          })
        )
        .subscribe(response => {
          if (!response) {
            return;
          }
          // Update offerType properties with the response, excluding Variants and VariantsCount
          Object.keys(response).forEach(key => {
            if (key !== 'Variants' && key !== 'VariantsCount') {
              offerType[key] = response[key];
            }
          });
        });
    }
  }

  onVariantChange(variantId: string, offerType: OfferGroupDomain<Offer>): void {
    this.fetchVariantDetails(variantId, offerType);
  }

  clearSelection() {
    this.selection.clear();
    this.listViewState.clearSelection();
  }

  syncSelectionWithDataSource() {
    const selectedRecords = this.listViewState.listViewStateValue.selectedRecords;
    this.selection.clear();
    this.eventOfferTypes.data.forEach(offer => {
      const isSelected = selectedRecords.some(record => record.DomainId === offer.DomainId);

      if (offer.DomainId && isSelected) {
        this.selection.select(offer);
      }
    });
  }

  updateStatus(status: Status, row: OfferGroupDomain<Offer>) {
    let selectedRecordsDetails: OfferGroupDomain<Offer>[] = [JSON.parse(JSON.stringify(row))];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<OfferGroupDomain<Offer>>, status: Status) {
    let details: Array<OfferCopy> = [];
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      details = selectedRecordsDetails.map((record: OfferGroupDomain<Offer>) => {

        let copy: OfferCopy = {
          Id: record.Detail.Id,
          Label: record.EventOfferType,
          Status: status as unknown as string
        };

        return copy;
      });
      this.offerpromoService.updateStatus(details)
        .pipe(
          catchError(error => {
            log.error('Error updating status', error);
            return [];
          })
        )
        .subscribe({
          next: () => {
            this.offerpromoService.reload();
            this.clearSelection();
            this.offerpromoService.updateRecordStatus(new Date());
          },
        });
    }
  }

  canEdit(): boolean {
    return !this.readOnlyMode;
  }

  /** Unique DOM id for comments anchor — variant rows use Detail.Id to avoid colliding with base DomainId. */
  commentsAnchorId(offer: OfferGroupDomain<Offer>): string {
    return commentsAnchorIdForOffer(offer);
  }

  openDialog($event: any, offer: OfferGroupDomain<Offer>) {
    if (!this.isCommentDialogOpened) {
      const commentElementStyle = offer.DomainId ? this.document.getElementById(offer.DomainId) as HTMLElement : null;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = false;
      dialogConfig.data = {
        headline: offer.Detail.Headline,
        channel: offer.EventOfferType,
        parentId: offer.Detail.Id,
        clientKey: offer.Detail.ClientKey,
        bodyCopy: offer.Detail.BodyCopy,
        showOfferCheckbox: true,
        workflowStage: this.workflowStages.length ? this.workflowStages[0] : null
      };
      const confirmDialog = this.dialog.open(CommentsComponent, dialogConfig);

      try {
        const instance = confirmDialog.componentInstance;
        instance.updateToOfferEmitter.subscribe(() => {
          this.updateStatus(Status.PENDING, offer);
        });
      } catch (error) {
        console.error('Error accessing instance:', error);
      }

      confirmDialog.afterOpened().subscribe(() => {
        if (commentElementStyle) {
          commentElementStyle.style.color = "blue";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(true);
        }
      });
      confirmDialog.afterClosed().subscribe((data) => {
        if (data > 0) {
          Object(offer).CommentsCount = data;
        }
        if (commentElementStyle) {
          commentElementStyle.style.color = "black";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(false);
        }
      });
    }
  }

  updateReadonlyMode(val: boolean) {
    this.readOnlyMode = val;
    this.offerpromoService.setOfferDetailsReadOnly(val);
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png'; // Path to your default image
  }

  isAllSelected(): boolean {
    return this.eventOfferTypes.data.every(offer => this.selection.isSelected(offer));
  }

  toggleSelect(checked: boolean, offer: OfferGroupDomain<Offer>) {
    if (checked) {
      this.selection.select(offer);
    } else {
      this.selection.deselect(offer);
    }

    if (offer.DomainId) {
      this.listViewState.toggleSelection(offer, 'DomainId');
    }
  }

  toggleSelectAll(checked: boolean): void {
    this.eventOfferTypes.data.forEach(offer => {
      if (!offer.DomainId) return;

      const isAlreadySelected = this.listViewState.isSelected(offer.DomainId, 'DomainId');

      if (checked && !isAlreadySelected) {
        this.selection.select(offer);
        this.listViewState.toggleSelection(offer, 'DomainId');
      } else if (!checked && isAlreadySelected) {
        this.selection.deselect(offer);
        this.listViewState.toggleSelection(offer, 'DomainId');
      }
    });
  }

  isSomeSelectedOnPage(): boolean {
    return this.eventOfferTypes.data.some(offer => this.selection.isSelected(offer));
  }

  getStatusClass(offer: OfferGroupDomain<Offer>): string {
    const status = offer?.Detail?.Status;
    return status === '0' ? 'statusPENDING' : 'status' + status;
  }

  getStatusText(offer: OfferGroupDomain<Offer>): string {
    const status = offer?.Detail?.Status;
    return status === '0' ? 'PENDING' : status || '';
  }

  isModifiedOffer(offer: OfferGroupDomain<Offer>): boolean {
    const offerId = offer?.Detail?.Id;
    return !!offerId && this._modifiedItemSet.has(offerId);
  }

  trackByFn(index: number, item: OfferGroupDomain<Offer>): number {
    return item.DomainId ? parseInt(item.DomainId, 10) : 0;
  }
}
